import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AuthBridge from "./auth/AuthBridge";
import AuthGate from "./auth/AuthGate";
import LayoutShell from "./components/LayoutShell";
import UsersPanel from "./panels/UsersPanel";
import PhilosophiesPanel from "./panels/PhilosophiesPanel";
import WorkoutPlansPanel from "./panels/WorkoutPlansPanel";
import AthleteProfilePanel from "./panels/AthleteProfilePanel";
import GlobalWorkoutsPanel from "./panels/GlobalWorkoutsPanel";
import PersonalizedWorkoutsPanel from "./panels/PersonalizedWorkoutsPanel";
import ManualWorkoutsPanel from "./panels/ManualWorkoutsPanel";
import PromptRunsPanel from "./panels/PromptRunsPanel";
import OngoingJobsPanel from "./panels/OngoingJobsPanel";
import NudgesPanel from "./panels/NudgesPanel";
import KillSwitchPanel from "./panels/KillSwitchPanel";
import MessagingEndpointsPanel from "./panels/MessagingEndpointsPanel";
import SmsConversationsPanel from "./panels/SmsConversationsPanel";
import SmsPendingPanel from "./panels/SmsPendingPanel";
import MessagesPanel from "./panels/MessagesPanel";
import FeedbackPanel from "./panels/FeedbackPanel";
import PersonasPanel from "./panels/PersonasPanel";
import AnalyticsPanel from "./panels/AnalyticsPanel";
import UserDashboardPanel from "./panels/UserDashboardPanel";
import { meApi } from "./api/routes";
import { PanelCacheProvider } from "./state/PanelCacheContext";

const panels = [
  { key: "users", label: "Users", description: "Accounts & auth fields", tag: "Core", component: UsersPanel },
  { key: "athlete", label: "Athlete Profile", description: "Context for coaching", tag: "Core", component: AthleteProfilePanel },
  { key: "plans", label: "Workout Plans", description: "Plan versions & goals", tag: "Training", component: WorkoutPlansPanel },
  { key: "philosophies", label: "Philosophies", description: "Distance-specific pillars", tag: "Training", component: PhilosophiesPanel },
  { key: "personas", label: "Personas", description: "Editable coaching voices", tag: "AI", component: PersonasPanel },
  { key: "global", label: "Global Workouts", description: "Reusable templates", tag: "Training", component: GlobalWorkoutsPanel },
  { key: "personalized", label: "Personalized", description: "User-specific sessions", tag: "Training", component: PersonalizedWorkoutsPanel },
  { key: "manual", label: "Manual Workouts", description: "Completed sessions entered by hand", tag: "History", component: ManualWorkoutsPanel },
  { key: "promptruns", label: "Prompt Runs", description: "LLM prompt executions", tag: "AI", component: PromptRunsPanel },
  { key: "analytics", label: "Analytics", description: "Usage, cost, and latency", tag: "Ops", component: AnalyticsPanel },
  { key: "ongoingJobs", label: "Ongoing Jobs", description: "LLM jobs by status", tag: "AI", component: OngoingJobsPanel },
  { key: "nudges", label: "Nudges", description: "Scheduled outbound nudges", tag: "Outbound", component: NudgesPanel },
  { key: "killSwitch", label: "Kill Switch", description: "Emergency cost and abuse controls", tag: "Ops", component: KillSwitchPanel },
  { key: "messagingEndpoints", label: "Endpoints", description: "Telegram and channel bindings", tag: "Messaging", component: MessagingEndpointsPanel },
  { key: "smsConversations", label: "Conversations", description: "SMS conversation state", tag: "Messaging", component: SmsConversationsPanel },
  { key: "smsPending", label: "Pending SMS", description: "Pending message per user", tag: "Messaging", component: SmsPendingPanel },
  { key: "smsMessages", label: "Messages", description: "SMS chat history", tag: "Messaging", component: MessagesPanel },
  { key: "feedback", label: "Feedback", description: "Support and product feedback", tag: "Ops", component: FeedbackPanel },
];

const userPanels = [
  { key: "dashboard", label: "Dashboard", description: "Plan progress and next training signal", tag: "Overview" },
  { key: "user", label: "User", description: "Account and messaging settings", tag: "Settings" },
  { key: "athlete", label: "Athlete", description: "Training context and coach profile", tag: "Settings" },
  { key: "plans", label: "Plans", description: "Current, past, and new workout plans", tag: "Training" },
  { key: "feedback", label: "Feedback", description: "Share product or coaching feedback", tag: "Support" },
];

const personaManagementPanels = [
  { key: "personas", label: "Personas", description: "Editable coaching voices", tag: "AI", component: PersonasPanel },
];

export default function App({ auth0Configured = false, devPreview = null }) {
  return (
    <PanelCacheProvider>
      <AuthGate auth0Configured={auth0Configured} devPreview={devPreview}>
        {auth0Configured ? <AuthBridge /> : null}
        {auth0Configured ? (
          <AuthenticatedConsoleWithAuth />
        ) : (
          <AuthenticatedConsole authUser={devPreview?.user} devPreview={devPreview} />
        )}
      </AuthGate>
    </PanelCacheProvider>
  );
}

function AuthenticatedConsoleWithAuth() {
  const { logout, user: authUser } = useAuth0();
  return (
    <AuthenticatedConsole
      authUser={authUser}
      onLogout={() =>
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        })
      }
    />
  );
}

function AuthenticatedConsole({ authUser, onLogout, devPreview = null }) {
  const requestedPanel = new URLSearchParams(window.location.search).get("panel");
  const requestedMode = new URLSearchParams(window.location.search).get("mode");
  const initialPanel = panels.some((panel) => panel.key === requestedPanel) ? requestedPanel : "users";
  const initialUserPanel = userPanels.some((panel) => panel.key === requestedPanel) ? requestedPanel : "dashboard";
  const [mode, setMode] = useState(requestedMode === "admin" ? "admin" : "user");
  const [isAdmin, setIsAdmin] = useState(false);
  const [canManagePersonas, setCanManagePersonas] = useState(false);
  const [active, setActive] = useState(initialPanel);
  const [userActive, setUserActive] = useState(initialUserPanel);
  const managementPanels = isAdmin ? panels : personaManagementPanels;
  const ActivePanel = useMemo(
    () => managementPanels.find((p) => p.key === active)?.component || managementPanels[0].component,
    [active, managementPanels]
  );
  const canOpenManagement = isAdmin || canManagePersonas;
  const effectiveMode = canOpenManagement ? mode : "user";
  const navItems = effectiveMode === "admin" ? managementPanels : userPanels;
  const activeNav = effectiveMode === "admin" ? active : userActive;
  const signedInName = authUser?.name || authUser?.email || "Signed in";

  useEffect(() => {
    let cancelled = false;
    meApi.get()
      .then((response) => {
        if (cancelled) return;
        const admin = Boolean(response.data?.isAdmin);
        const personaManager = Boolean(response.data?.canManagePersonas);
        if (response.data?.access && response.data.access.accepted === false) {
          window.location.assign("/onboarding");
          return;
        }
        setIsAdmin(admin);
        setCanManagePersonas(personaManager);
        if (!admin && !personaManager && mode === "admin") {
          setMode("user");
        } else if (!admin && personaManager && mode === "admin") {
          setActive("personas");
        }
      })
      .catch(() => {
        if (!cancelled) setMode("user");
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return (
    <LayoutShell
      mode={effectiveMode}
      navItems={navItems}
      activeNav={activeNav}
      onNavChange={effectiveMode === "admin" ? setActive : setUserActive}
      signedInName={signedInName}
      onLogout={onLogout}
      devPreview={devPreview}
      modeSwitch={
        canOpenManagement ? (
          <div className="mode-switcher">
            <button className={mode === "user" ? "active" : ""} onClick={() => setMode("user")}>User panel</button>
            <button className={mode === "admin" ? "active" : ""} onClick={() => { if (!isAdmin) setActive("personas"); setMode("admin"); }}>{isAdmin ? "Admin panel" : "Persona panel"}</button>
          </div>
        ) : null
      }
    >
      {mode === "admin" && canOpenManagement ? (
        <ActivePanel />
      ) : (
        <UserDashboardPanel activeTab={userActive} onTabChange={setUserActive} />
      )}
    </LayoutShell>
  );
}
